import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, CardContent } from '@mlm/ui';
import { listInventoryBatches, type InventoryBatchKind } from '../../lib/api';

/**
 * The "document log" for a branch — one row per receive shipment / write-off
 * report, with the same printable receipt available behind each row's Print
 * action. Replaces the per-movement noise of the raw inventory_movements log
 * for admin-initiated batches.
 */
export function BatchesSubview({ branchId }: { branchId: string }) {
  const [kind, setKind] = useState<InventoryBatchKind | 'all'>('all');

  const batches = useQuery({
    queryKey: ['admin-inventory-batches', branchId, kind],
    queryFn: () =>
      listInventoryBatches(branchId, {
        kind: kind === 'all' ? undefined : kind,
        limit: 100,
      }),
  });

  function openPrint(batchId: string) {
    window.open(`/branches/${branchId}/batches/${batchId}/print`, '_blank', 'noopener');
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1.5">
          {(['all', 'receive', 'write_off'] as const).map((k) => (
            <Button
              key={k}
              size="sm"
              variant={kind === k ? 'default' : 'outline'}
              onClick={() => setKind(k)}
            >
              {k === 'all' ? 'All' : k === 'receive' ? 'Receives' : 'Write-offs'}
            </Button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {batches.data?.length ?? 0} {batches.data?.length === 1 ? 'document' : 'documents'}
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          {batches.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : (batches.data?.length ?? 0) === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              No {kind === 'all' ? '' : kind === 'receive' ? 'receives' : 'write-offs'} yet.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium">Ref</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">By</th>
                  <th className="px-4 py-2 font-medium">Memo</th>
                  <th className="px-4 py-2 font-medium text-right">Lines</th>
                  <th className="px-4 py-2 font-medium text-right">Units</th>
                  <th className="px-4 py-2 font-medium w-20" />
                </tr>
              </thead>
              <tbody>
                {batches.data!.map((b) => (
                  <tr key={b.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-2 font-mono text-xs">
                      <button
                        type="button"
                        onClick={() => openPrint(b.id)}
                        className="hover:underline"
                      >
                        {b.refNumber}
                      </button>
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant={b.kind === 'receive' ? 'secondary' : 'outline'}>
                        {b.kind === 'receive' ? 'Receive' : 'Write-off'}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(b.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {b.actorName ?? b.actorEmail ?? ''}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {b.kind === 'write_off' && b.reason ? (
                        <span className="inline-block mr-1 capitalize">{b.reason.replace(/_/g, ' ')}.</span>
                      ) : null}
                      {b.memo ?? ''}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{b.lineCount}</td>
                    <td
                      className={`px-4 py-2 text-right tabular-nums font-medium ${
                        b.kind === 'receive' ? 'text-success' : 'text-destructive'
                      }`}
                    >
                      {b.kind === 'receive' ? '+' : '−'}
                      {b.unitCount}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => openPrint(b.id)}
                        className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                      >
                        Print
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
